import { useState, useEffect, useRef } from 'react';
import { Modal, Box, TextField, Button, Alert } from '@mui/material';
import { knowledgeApiError } from '../utils/knowledgeApiError';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import ReactQuill from 'react-quill';
import Quill from 'quill';
import { marked } from 'marked';
import 'react-quill/dist/quill.snow.css';
import '../translations.js';
import './AddFaqFromHistoryModal.css';

const Delta = Quill.import('delta');

const TITLE_MAX_CHARS = 100;
const QUESTION_MAX_CHARS = 250;
const ANSWER_MAX_CHARS = 2000;

// Remove common empty-paragraph artifacts so we don't save junk
function cleanHtmlForQuill(html) {
  let s = (html || '').trim();
  // trailing empty paras
  s = s.replace(/(?:\s*<p><br><\/p>\s*)+$/gi, '');
  // empty paras between paragraph and list (defensive)
  s = s.replace(/<\/p>\s*(?:<p><br><\/p>\s*)+(?=<\s*(ul|ol)\b)/gi, '</p>');
  // leading empty para
  s = s.replace(/^\s*<p><br><\/p>\s*/i, '');
  return s;
}

// Clipboard matcher to DROP the specific empty <p> Quill inserts between <p> and the following <ul>/<ol>
function paragraphMatcher(node, delta) {
  const isEmptyPara =
    node.tagName === 'P' &&
    node.childNodes.length === 1 &&
    node.firstChild &&
    node.firstChild.nodeName === 'BR';

  if (!isEmptyPara) return delta;

  const prev = node.previousElementSibling;
  const next = node.nextElementSibling;
  const prevIsParagraph = prev && prev.tagName === 'P';
  const nextIsList = next && (next.tagName === 'UL' || next.tagName === 'OL');

  if (prevIsParagraph && nextIsList) {
    return new Delta(); // skip this node entirely
  }
  return delta;
}

// helper to compute plain text length from HTML (for initial counter)
function htmlToPlainLength(html) {
  if (!html) return 0;
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (div.textContent || div.innerText || '').replace(/\n$/, ''); // drop trailing NL
  return text.length;
}

const AddFaqFromHistoryModal = ({ open, onClose, onSaved, question, answer, botId }) => {
  const [faqTitle, setFaqTitle] = useState('');
  const [faqQuestion, setFaqQuestion] = useState('');
  const [faqAnswer, setFaqAnswer] = useState('');      // HTML we will save
  const [initialHtml, setInitialHtml] = useState('');  // seeds editor
  const [editorKey, setEditorKey] = useState(0);       // force fresh mount per open
  const [answerCharsUsed, setAnswerCharsUsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const quillRef = useRef(null);

  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!open) return;

    setFaqTitle('');
    setSaveError(null);
    setFaqQuestion(question || '');

    // Markdown → HTML, light sanitize (clipboard matcher handles the rest)
    const converted = answer ? marked.parse(answer) : '';
    const sanitized = cleanHtmlForQuill(converted);

    setInitialHtml(sanitized);      // defaultValue for editor
    setFaqAnswer(sanitized);        // value to save later
    setAnswerCharsUsed(htmlToPlainLength(sanitized)); // initial counter
    setEditorKey((k) => k + 1);     // remount Quill to avoid re-import artifacts each open
  }, [open, question, answer]);

  const handleSave = async () => {
    if (!faqTitle.trim() || !faqQuestion.trim() || !faqAnswer.trim()) return;

    setLoading(true);
    setSaveError(null);
    const startTime = Date.now();
    try {
      const payload = {
        bot_id: botId,
        faq_items: [
          {
            // NOTE: faqAnswer is HTML (WYSIWYG)
            faq_text: `Question:\n${faqQuestion.trim()}\n\nAnswer:\n${faqAnswer.trim()}\n`,
            faq_description: faqTitle
          }
        ],
        user_id: localStorage.getItem('user_id')
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}ingest_faq_items`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) throw await knowledgeApiError(response, t('mcp_settings_save_failed'));

      // Ensure spinner shows for at least 2000ms
      const elapsed = Date.now() - startTime;
      if (elapsed < 2000) {
        await new Promise(resolve => setTimeout(resolve, 2000 - elapsed));
      }

      if (onSaved) {
        onSaved();
      } else {
        onClose();
      }
    } catch (err) {
      console.error(err);
      setSaveError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const titleCharsUsed = faqTitle.length;
  const questionCharsUsed = faqQuestion.length;

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link']
    ],
    clipboard: {
      matchVisual: false,
      matchers: [['P', paragraphMatcher]]
    }
  };

  // Enforce character limit on visible text
  const handleEditorChange = (html, delta, source, editor) => {
    const quill = quillRef.current ? quillRef.current.getEditor() : null;
    let visibleLen = editor.getLength() - 1; // Quill adds a trailing newline

    if (source === 'user' && quill && visibleLen > ANSWER_MAX_CHARS) {
      // Delete the overflow characters from the end
      quill.deleteText(ANSWER_MAX_CHARS, visibleLen - ANSWER_MAX_CHARS);
      visibleLen = ANSWER_MAX_CHARS;

      const currentHtml = cleanHtmlForQuill(quill.root.innerHTML);
      setFaqAnswer(currentHtml);
      setAnswerCharsUsed(visibleLen);
      return;
    }

    setFaqAnswer(cleanHtmlForQuill(html));
    setAnswerCharsUsed(Math.max(0, visibleLen));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: isMobile ? 2.5 : 4,
          width: isMobile ? '80%' : '90%',
          maxWidth: 600,
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>{t('add_qa')}</h3>
        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

        <TextField
          label={t('faq_title_label')}
          fullWidth
          margin="normal"
          value={faqTitle}
          onChange={(e) => setFaqTitle(e.target.value)}
          inputProps={{ maxLength: TITLE_MAX_CHARS }}
          helperText={`${titleCharsUsed}/${TITLE_MAX_CHARS}`}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root.Mui-focused fieldset': {
              borderColor: '#515151',
            },
            '& .MuiInputLabel-root': {
              color: 'gray',
              '&.Mui-focused': { color: '#5616ea' },
            },
          }}
        />

        <TextField
          label={t('faq_question_label')}
          fullWidth
          margin="normal"
          value={faqQuestion}
          onChange={(e) => setFaqQuestion(e.target.value)}
          inputProps={{ maxLength: QUESTION_MAX_CHARS }}
          helperText={`${questionCharsUsed}/${QUESTION_MAX_CHARS}`}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root.Mui-focused fieldset': {
              borderColor: '#515151',
            },
            '& .MuiInputLabel-root': {
              color: 'gray',
              '&.Mui-focused': { color: '#5616ea' },
            },
          }}
        />

        {/* WYSIWYG Answer Field with hard char limit */}
        <div className="faq-editor-wrap">
          <label className="faq-editor-label">{t('faq_answer_label')}</label>

          <ReactQuill
            ref={quillRef}
            key={editorKey}
            defaultValue={initialHtml}
            onChange={handleEditorChange}
            modules={quillModules}
            formats={['bold', 'italic', 'underline', 'list', 'bullet', 'link']}
            className="faq-editor"
            readOnly={loading}
          />

          <div className={`faq-char-counter ${answerCharsUsed >= ANSWER_MAX_CHARS ? 'limit' : ''}`}>
            {answerCharsUsed}/{ANSWER_MAX_CHARS}
          </div>
          {answerCharsUsed >= ANSWER_MAX_CHARS && (
            <div className="faq-editor-error">{t('faq_answer_too_long')}</div>
          )}
        </div>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              color: '#5616ea',
              borderColor: '#5616ea',
              '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
            }}
            >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || !faqTitle.trim() || !faqQuestion.trim() || !faqAnswer.trim() || answerCharsUsed > ANSWER_MAX_CHARS}
            sx={{
              ml: 1,
              backgroundColor: '#5616ea',
              '&:hover': { backgroundColor: '#4a14d6' },
              minWidth: 80,
            }}
          >
            {loading ? <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: 16 }} /> : t('add')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddFaqFromHistoryModal;
